import React, { useEffect, useState } from "react";
import { useGetCreditPaidQuery } from "../../../redux/api/modules/billingApi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../shared/CustomTable";
import CustomPagination from "../../../shared/CustomPagination";
import { CreditReceiptColumns } from "../../../constants/BillingConstants";
import { EditIcon, PrintIcon } from "../../../utils/TableActionIcons";
import BillingFilters from "../../../components/his/billing/BillingFilters";
import usePersistentBillingFilters from "../../../hooks/usePersistentBillingFilters";
import PayCreditForm from "../depost/PayCreditForm";
import CheckPermission from "../../../hooks/CheckPermission";
import { useSelector } from 'react-redux';
import { hasBillingSearchFilters, omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from '../../../utils/businessLevelAccess';
const ReuseableModal = React.lazy(() => import("../../../shared/ReuseableModal"));

function CreditPaid() {
    const authUser = useSelector(state => state?.auth?.user);
    const skipInitialBillingLoad = shouldSkipInitialBillingLoad(authUser);
    const [payload, setPayload, resetPayload, appliedPayload, applyFilters] = usePersistentBillingFilters('creditPaid', { per_page: 20 });

    const queryPayload = React.useMemo(() => ({
        patient_name: appliedPayload.query || "",
        file_number: appliedPayload.file_number || appliedPayload.filters?.file_number || "",
        document_id: appliedPayload.document_id || appliedPayload.filters?.document_id || "",
        receipt_number: appliedPayload.filters?.receipt_number || appliedPayload.filters?.invoice_number || "",
        department_id: appliedPayload.filters?.department_id || "",
        start_date: appliedPayload.start_date,
        end_date: appliedPayload.end_date,
        page: appliedPayload.page,
        per_page: appliedPayload.per_page
    }), [appliedPayload]);

    const {
        data,
        isLoading: queryLoading,
        isFetching: queryFetching,
        refetch
    } = useGetCreditPaidQuery(queryPayload, {
        skip: skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)
    });

    const loading = queryLoading || queryFetching;

    const [searchQuery, setSearchQuery] = useState(payload?.query || "");
    const [editCreditPayment, setEditCreditPayment] = useState(null);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { hasPermission } = CheckPermission();

    const fetchInitialData = () => {
        refetch();
    };

    

    const onPageChange = async (newPage, newRowsPerPage) => {
        setPayload((prevPayload) => ({
            ...prevPayload,
            page: newPage,
            per_page: newRowsPerPage,
        }));
    };

    const actionBodyTempelate = (rowData) => {
        return (
            <div className="flex gap-2 justify-center">
                <PrintIcon
                    title={t("Print Receipt")}
                    iconSize="20"
                    onClick={() =>
                        navigate(`/print-invoice/${rowData?.receipt_number}`, {
                            state: {
                                invoiceNumber: rowData?.receipt_number,
                                activeTabIndex: 5,
                                activeChildTabIndex: 1,
                                creditPaid: true
                            },
                        })
                    }
                />
                {hasPermission("patientCreditBalance", "update") && (
                    <EditIcon
                        title={t("Update Credit Payment")}
                        iconSize="18"
                        onClick={() => setEditCreditPayment(rowData)}
                    />
                )}
            </div>
        );
    };

    const actions = {
        header: t("Actions"),
        body: actionBodyTempelate,
        headerCellClass: 'flex items-center justify-center'
    };

    const columnsData = [actions, ...CreditReceiptColumns(t, true)];

    return (
        <div className="">
            <BillingFilters
                applyFilters={applyFilters}
                setSearchQuery={setSearchQuery}
                fetchInitialData={fetchInitialData}
                searchQuery={searchQuery}
                payloadData={payload}
                setPayloadData={setPayload}
                refetch={refetch}
                invoiceType="creditPaid"
                resetFilters={() => {
                    resetPayload();
                    setSearchQuery("");
                }}
            />
            <div>
                <CustomTable
                    isLoading={loading}
                    data={data?.data || []}
                    columns={columnsData}
                />
                {data?.data?.length > 0 && !loading && (
                    <CustomPagination
                        currentPage={data?.page || payload?.page || 1}
                        rowsPerPage={data?.per_page || payload?.per_page || 20}
                        totalRecords={data?.total_records || data?.totalRecords || 0}
                        totalPages={data?.total_pages || data?.totalPages || 1}
                        onPageChange={onPageChange}
                    />
                )}
            </div>
            <ReuseableModal
                isVisible={Boolean(editCreditPayment)}
                onClose={() => setEditCreditPayment(null)}
                title={t("Update Credit Payment")}
                position="top"
            >
                {editCreditPayment && (
                    <PayCreditForm
                        selectedPatient={{
                            patient_id: editCreditPayment?.patient_id,
                            full_name: editCreditPayment?.patient_name,
                            document_id: editCreditPayment?.patient_document_id,
                            file_no: editCreditPayment?.patient_file_no,
                            patient_file_no: editCreditPayment?.patient_file_no,
                        }}
                        invoiceData={editCreditPayment}
                        mode="update"
                        setOpenPayCreditForm={(visible) => {
                            if (!visible) setEditCreditPayment(null);
                        }}
                        onPayCreditSuccess={() => {
                            setEditCreditPayment(null);
                            fetchInitialData();
                        }}
                    />
                )}
            </ReuseableModal>
        </div>
    );
}

export default CreditPaid;
